import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  MenuItem,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Divider,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  getAccounts,
  getPortfolios,
  createTrade,
  getPortfolioTargetAllocations,
  preflightPortfolioTrade,
} from '../services/api';
import { useNavigate } from 'react-router-dom';
import {
  MARKET_OPERATIONS,
  TRADE_OPERATION_LABELS,
  ALL_TRADE_OPERATIONS,
} from '../constants/tradeOperations';

const TradeForm = ({
  onSuccess,
  editTrade: _editTrade,
  defaultAccountId = null,
  defaultPortfolioId = null,
  lockAccountPortfolio = false,
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    account: '',
    portfolio: '',
    instrument: '',
    quantity: '',
    operation: 'buy',
    input_type: 'units',
    amount: '',
    currency: 'USD',
    notes: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [loadingStates, setLoadingStates] = useState({
    accounts: false,
    portfolios: false,
    instruments: false,
  });
  const [preflightDialog, setPreflightDialog] = useState(null);

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (defaultAccountId) {
      setFormData((prev) => ({
        ...prev,
        account: defaultAccountId,
        portfolio: defaultPortfolioId || prev.portfolio,
      }));
    }
  }, [defaultAccountId, defaultPortfolioId]);

  useEffect(() => {
    if (formData.account) {
      fetchPortfolios(formData.account);
    } else {
      setPortfolios([]);
    }
  }, [formData.account]);

  useEffect(() => {
    if (formData.portfolio) {
      fetchPortfolioInstruments(formData.portfolio);
    } else {
      setInstruments([]);
    }
  }, [formData.portfolio]);

  useEffect(() => {
    if (defaultPortfolioId) {
      fetchPortfolioInstruments(defaultPortfolioId);
    }
  }, [defaultPortfolioId]);

  const fetchAccounts = async () => {
    try {
      setLoadingStates((prev) => ({ ...prev, accounts: true }));
      const data = await getAccounts();
      setAccounts(data);
    } catch (err) {
      setError('Failed to fetch accounts');
      console.error('Error fetching accounts:', err);
    } finally {
      setLoadingStates((prev) => ({ ...prev, accounts: false }));
    }
  };

  const fetchPortfolios = async (accountId) => {
    try {
      setLoadingStates((prev) => ({ ...prev, portfolios: true }));
      const data = await getPortfolios(accountId);
      setPortfolios(data);
    } catch (err) {
      setError('Failed to fetch portfolios');
      console.error('Error fetching portfolios:', err);
    } finally {
      setLoadingStates((prev) => ({ ...prev, portfolios: false }));
    }
  };

  const fetchPortfolioInstruments = async (portfolioId) => {
    try {
      setLoadingStates((prev) => ({ ...prev, instruments: true }));
      const data = await getPortfolioTargetAllocations(portfolioId);
      const list = (data.items || [])
        .filter((row) => row.active !== false && row.instrument)
        .map((row) => row.instrument);
      setInstruments(list);
      if (!list.length) {
        setError('This portfolio has no active allocation rules. Add rules before trading.');
      }
    } catch (err) {
      console.error('Error fetching portfolio instruments:', err.response?.data || err.message);
      setError('Failed to load instruments for this portfolio');
      setInstruments([]);
    } finally {
      setLoadingStates((prev) => ({ ...prev, instruments: false }));
    }
  };

  const isMarketOp = MARKET_OPERATIONS.includes(formData.operation);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'account') {
      setFormData((prev) => ({
        ...prev,
        account: value,
        portfolio: '',
        instrument: '',
        quantity: '',
        amount: '',
        notes: '',
      }));
      setInstruments([]);
    } else if (name === 'portfolio') {
      setFormData((prev) => ({
        ...prev,
        portfolio: value,
        instrument: '',
        quantity: '',
        amount: '',
        calculated_amount: '',
        calculated_quantity: '',
        notes: '',
      }));
    } else if (name === 'instrument') {
      const selectedInstrument = instruments.find((inst) => inst.id === parseInt(value, 10));
      setFormData((prev) => ({
        ...prev,
        instrument: value,
        currency: selectedInstrument?.currency || 'USD',
      }));
    } else if (name === 'operation') {
      setFormData((prev) => ({
        ...prev,
        operation: value,
        quantity: '',
        amount: '',
        calculated_amount: '',
        calculated_quantity: '',
        notes: '',
      }));
    } else if (name === 'input_type') {
      setFormData((prev) => ({
        ...prev,
        input_type: value,
        quantity: value === 'units' ? prev.quantity : '',
        amount: value === 'amount' ? prev.amount : '',
        calculated_amount: '',
        calculated_quantity: '',
      }));
    } else if (name === 'quantity' && isMarketOp) {
      const selectedInstrument = instruments.find((inst) => inst.id === parseInt(formData.instrument, 10));
      const price = selectedInstrument?.price || 0;
      const calculatedAmount = value && price ? parseFloat(value) * parseFloat(price) : '';
      setFormData((prev) => ({
        ...prev,
        quantity: value,
        amount: '',
        calculated_amount: calculatedAmount !== '' ? calculatedAmount.toFixed(2) : '',
      }));
    } else if (name === 'amount' && isMarketOp && formData.input_type === 'amount') {
      const selectedInstrument = instruments.find((inst) => inst.id === parseInt(formData.instrument, 10));
      const price = selectedInstrument?.price || 0;
      const calculatedQuantity =
        value && price && parseFloat(price) > 0 ? parseFloat(value) / parseFloat(price) : '';
      setFormData((prev) => ({
        ...prev,
        amount: value,
        quantity: '',
        calculated_quantity: calculatedQuantity !== '' ? calculatedQuantity.toFixed(2) : '',
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const buildTradePayload = () => {
    const selectedInstrument = instruments.find((inst) => inst.id === parseInt(formData.instrument, 10));
    const selectedPortfolio = portfolios.find((p) => p.id === parseInt(formData.portfolio, 10));
    if (!selectedInstrument || !selectedPortfolio) return null;

    const base = {
      portfolio_id: parseInt(formData.portfolio, 10),
      account_id: parseInt(formData.account, 10),
      instrument_id: parseInt(formData.instrument, 10),
      operation: formData.operation,
      currency: selectedInstrument.currency,
      notes: formData.notes || '',
    };

    const instPrice = parseFloat(selectedInstrument.price || 0);

    if (MARKET_OPERATIONS.includes(formData.operation)) {
      const price = instPrice;
      const out = { ...base, price };
      if (formData.input_type === 'units') {
        out.quantity = parseFloat(formData.quantity);
      } else {
        out.amount = parseFloat(formData.amount);
      }
      return out;
    }

    if (formData.operation === 'dividend_cash') {
      return {
        ...base,
        price: 0,
        amount: parseFloat(formData.amount),
      };
    }

    if (formData.operation === 'dividend_stock') {
      return {
        ...base,
        price: 0,
        quantity: parseFloat(formData.quantity),
      };
    }

    if (formData.operation === 'stock_split') {
      return {
        ...base,
        price: 0,
        amount: parseFloat(formData.amount),
      };
    }

    const corp = {
      ...base,
      price: instPrice > 0 ? instPrice : 0,
    };
    if (formData.quantity) corp.quantity = parseFloat(formData.quantity);
    if (formData.amount) corp.amount = parseFloat(formData.amount);
    return corp;
  };

  const validateForm = useCallback(() => {
    if (!formData.portfolio || !formData.instrument || !formData.account) {
      return 'Please select account, portfolio, and instrument.';
    }

    const selectedInstrument = instruments.find((inst) => inst.id === parseInt(formData.instrument, 10));
    if (!selectedInstrument) return 'Selected instrument not found.';

    const op = formData.operation;

    if (MARKET_OPERATIONS.includes(op)) {
      const price = parseFloat(selectedInstrument.price || 0);
      if (!price || price <= 0) {
        return 'Buys and sells require a positive instrument price. Update the instrument or pick another one.';
      }
      if (formData.input_type === 'units') {
        const q = parseFloat(formData.quantity);
        if (!formData.quantity || Number.isNaN(q) || q <= 0) return 'Enter a valid quantity.';
      } else {
        const a = parseFloat(formData.amount);
        if (!formData.amount || Number.isNaN(a) || a <= 0) return 'Enter a valid amount.';
      }
      return null;
    }

    if (op === 'dividend_cash') {
      const a = parseFloat(formData.amount);
      if (!formData.amount || Number.isNaN(a) || a <= 0) return 'Cash dividend requires a positive amount.';
      return null;
    }

    if (op === 'dividend_stock') {
      const q = parseFloat(formData.quantity);
      if (!formData.quantity || Number.isNaN(q) || q <= 0) return 'Stock dividend requires a positive share quantity.';
      return null;
    }

    if (op === 'stock_split') {
      const ratio = parseFloat(formData.amount);
      if (!formData.amount || Number.isNaN(ratio) || ratio <= 0) return 'Enter the split ratio as a positive number (e.g. 2 for 2:1).';
      return null;
    }

    if (['merger', 'spinoff', 'other_corporate'].includes(op)) {
      if (!String(formData.notes || '').trim()) {
        return 'Describe the corporate action in Notes (terms, ratio, counterparty).';
      }
      const hasQ = formData.quantity && parseFloat(formData.quantity) > 0;
      const hasA = formData.amount && parseFloat(formData.amount) > 0;
      if (!hasQ && !hasA) return 'Provide quantity and/or amount as applicable.';
      return null;
    }

    return 'Unsupported operation.';
  }, [formData, instruments]);

  const submitTrade = async (tradeData) => {
    setSubmitting(true);
    try {
      await createTrade(tradeData);
      setPreflightDialog(null);
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/trades', { replace: true });
      }
    } catch (apiError) {
      const errorData = apiError.response?.data;
      if (errorData && typeof errorData === 'object') {
        const errorMessages = Object.entries(errorData)
          .map(([field, errors]) => {
            if (Array.isArray(errors)) return `${field}: ${errors.join(', ')}`;
            if (typeof errors === 'object') return `${field}: ${JSON.stringify(errors)}`;
            return `${field}: ${errors}`;
          })
          .join('\n');
        setError(`Failed to create trade:\n${errorMessages}`);
      } else {
        setError(`Failed to create trade: ${apiError.message || 'Unknown error'}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const msg = validateForm();
    if (msg) {
      setError(msg);
      return;
    }

    const tradeData = buildTradePayload();
    if (!tradeData) {
      setError('Could not build trade payload.');
      return;
    }

    if (formData.operation === 'buy') {
      try {
        const preflight = await preflightPortfolioTrade(formData.portfolio, {
          operation: 'buy',
          instrument_id: tradeData.instrument_id,
          price: tradeData.price,
          quantity: tradeData.quantity,
          amount: tradeData.amount,
        });
        if (!preflight.can_proceed) {
          setPreflightDialog({
            type: 'block',
            title: 'Cannot place buy',
            messages: (preflight.blockers || []).map((b) => b.message),
          });
          return;
        }
        if (preflight.warnings?.length) {
          setPreflightDialog({
            type: 'warn',
            title: 'Cash allocation warning',
            messages: preflight.warnings.map((w) => w.message),
            tradeData,
          });
          return;
        }
      } catch (preflightErr) {
        setError('Could not validate trade against portfolio rules.');
        console.error(preflightErr);
        return;
      }
    } else if (formData.portfolio && formData.instrument) {
      try {
        const preflight = await preflightPortfolioTrade(formData.portfolio, {
          operation: formData.operation,
          instrument_id: parseInt(formData.instrument, 10),
          price: tradeData.price,
          quantity: tradeData.quantity,
          amount: tradeData.amount,
        });
        if (!preflight.can_proceed) {
          setPreflightDialog({
            type: 'block',
            title: 'Cannot place trade',
            messages: (preflight.blockers || []).map((b) => b.message),
          });
          return;
        }
      } catch (preflightErr) {
        setError('Could not validate trade against portfolio rules.');
        console.error(preflightErr);
        return;
      }
    }

    await submitTrade(tradeData);
  };

  const handlePreflightProceed = async () => {
    if (preflightDialog?.tradeData) {
      await submitTrade(preflightDialog.tradeData);
    }
  };

  const selectedAccount = accounts.find((acc) => acc.id === formData.account);
  const selectedInstrument = instruments.find((inst) => inst.id === formData.instrument);

  const submitDisabled = useMemo(
    () => submitting || Boolean(validateForm()),
    [submitting, validateForm]
  );

  const quantityAmountSection = () => {
    if (!isMarketOp) {
      if (formData.operation === 'dividend_cash') {
        return (
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              type="number"
              name="amount"
              label="Dividend amount"
              value={formData.amount}
              onChange={handleChange}
              helperText={`Cash received, in ${selectedInstrument?.currency || 'USD'}`}
            />
          </Grid>
        );
      }
      if (formData.operation === 'dividend_stock') {
        return (
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              type="number"
              name="quantity"
              label="Shares received"
              value={formData.quantity}
              onChange={handleChange}
            />
          </Grid>
        );
      }
      if (formData.operation === 'stock_split') {
        return (
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              type="number"
              name="amount"
              label="Split ratio"
              value={formData.amount}
              onChange={handleChange}
              helperText="e.g. 2 for a 2:1 split (stored as amount for valuation logic)"
            />
          </Grid>
        );
      }
      if (['merger', 'spinoff', 'other_corporate'].includes(formData.operation)) {
        return (
          <>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                multiline
                minRows={3}
                name="notes"
                label="Corporate action details"
                value={formData.notes}
                onChange={handleChange}
                helperText="Describe terms, ratio, counterparty instrument, etc."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                name="quantity"
                label="Quantity (optional)"
                value={formData.quantity}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                name="amount"
                label="Amount (optional)"
                value={formData.amount}
                onChange={handleChange}
                helperText={`In ${selectedInstrument?.currency || 'USD'} if applicable`}
              />
            </Grid>
          </>
        );
      }
      return null;
    }

    return (
      <>
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              name="input_type"
              value={formData.input_type}
              onChange={handleChange}
              sx={{ mb: 2 }}
            >
              <FormControlLabel value="units" control={<Radio />} label="Enter units" />
              <FormControlLabel value="amount" control={<Radio />} label="Enter amount" />
            </RadioGroup>
          </FormControl>
        </Grid>
        {formData.input_type === 'units' ? (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                type="number"
                name="quantity"
                label="Quantity"
                value={formData.quantity}
                onChange={handleChange}
                disabled={!formData.instrument}
                helperText="Number of units"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Calculated amount"
                value={formData.calculated_amount || ''}
                disabled
                helperText={`In ${selectedInstrument?.currency || 'USD'}`}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>{selectedInstrument?.currency || 'USD'}</Typography>,
                }}
              />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                type="number"
                name="amount"
                label="Amount"
                value={formData.amount}
                onChange={handleChange}
                disabled={!formData.instrument}
                helperText={`Total in ${selectedInstrument?.currency || 'USD'}`}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>{selectedInstrument?.currency || 'USD'}</Typography>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Calculated quantity"
                value={formData.calculated_quantity || ''}
                disabled
              />
            </Grid>
          </>
        )}
      </>
    );
  };

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          New trade
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} style={{ whiteSpace: 'pre-wrap' }}>
            {error}
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Account
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                select
                name="account"
                label="Account"
                value={formData.account}
                onChange={handleChange}
                disabled={loadingStates.accounts || lockAccountPortfolio}
                helperText={selectedAccount?.account_number}
                InputProps={{
                  endAdornment: loadingStates.accounts && <CircularProgress size={20} />,
                }}
              >
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name} ({account.account_number})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                select
                name="portfolio"
                label="Portfolio"
                value={formData.portfolio}
                onChange={handleChange}
                disabled={!formData.account || loadingStates.portfolios || lockAccountPortfolio}
                helperText={!formData.account ? 'Select an account first' : ''}
                InputProps={{
                  endAdornment: loadingStates.portfolios && <CircularProgress size={20} />,
                }}
              >
                {portfolios.map((portfolio) => (
                  <MenuItem key={portfolio.id} value={portfolio.id}>
                    {portfolio.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Trade details
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="instrument-label">Instrument</InputLabel>
                <Select
                  labelId="instrument-label"
                  name="instrument"
                  value={formData.instrument}
                  onChange={handleChange}
                  disabled={!formData.portfolio || loadingStates.instruments}
                  label="Instrument"
                  required
                >
                  {instruments.map((instrument) => (
                    <MenuItem key={instrument.id} value={instrument.id}>
                      {instrument.name} ({instrument.symbol})
                    </MenuItem>
                  ))}
                </Select>
                {!formData.portfolio && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Select a portfolio first
                  </Typography>
                )}
                {formData.portfolio && !loadingStates.instruments && instruments.length === 0 && (
                  <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                    No instruments in portfolio allocation rules
                  </Typography>
                )}
                {formData.portfolio && instruments.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Only funds in this portfolio&apos;s allocation rules
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="operation-label">Operation</InputLabel>
                <Select
                  labelId="operation-label"
                  name="operation"
                  value={formData.operation}
                  onChange={handleChange}
                  disabled={!formData.instrument}
                  label="Operation"
                  required
                >
                  {ALL_TRADE_OPERATIONS.map((op) => (
                    <MenuItem key={op} value={op}>
                      {TRADE_OPERATION_LABELS[op] || op}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {quantityAmountSection()}

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={submitDisabled}
              >
                {submitting ? <CircularProgress size={24} /> : 'Create trade'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Dialog
        open={Boolean(preflightDialog)}
        onClose={() => setPreflightDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{preflightDialog?.title || 'Trade validation'}</DialogTitle>
        <DialogContent>
          {(preflightDialog?.messages || []).map((text) => (
            <DialogContentText key={text} sx={{ mb: 1 }}>
              {text}
            </DialogContentText>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreflightDialog(null)}>
            {preflightDialog?.type === 'warn' ? 'Cancel' : 'OK'}
          </Button>
          {preflightDialog?.type === 'warn' && (
            <Button
              variant="contained"
              color="warning"
              onClick={handlePreflightProceed}
              disabled={submitting}
            >
              Proceed anyway
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TradeForm;
